// ============================================
// DailyClosePage — Chốt cuối ngày (sheet 2 mục #53)
// ============================================
//
// Một cửa hàng một ngày; PM đã BỎ khái niệm "ca". Vì thế trang bắt buộc phải có
// một cửa hàng cụ thể — không có bản "toàn chuỗi".
//
// ⚠️ Ranh giới ngày ở đây cắt theo MÚI GIỜ CỦA CỬA HÀNG, còn trang Reports
// (/reports/daily-sales/) gom theo giờ server. Đơn phát sinh sát nửa đêm có thể
// rơi vào hai ngày khác nhau giữa hai báo cáo. Đó là hành vi ĐÚNG theo thiết kế,
// không phải bug — nên trang nói thẳng điều đó ra thay vì để người dùng tự nghi
// ngờ số liệu.

import { useState, type ReactNode } from 'react';
import { PageContainer, PageHeader } from '../../components/shared';
import { useStoreSelectionStore } from '../../stores';
import { useDailyClose } from '../../hooks';
import { useMoney } from '../../providers/CurrencyProvider';
import { useI18n } from '../../i18n';

/** Chuỗi decimal của BE -> số để format. Không parse được thì coi như 0. */
function toNumber(value: string | number | null | undefined): number {
  if (value == null) return 0;
  const parsed = typeof value === 'number' ? value : Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function WarningIcon({ className }: { className?: string }) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" className={className}>
      <path
        d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
      <path d="M12 9v4M12 17h.01" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

function SectionCard({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="rounded-xl border border-slate-100 bg-white p-5 shadow-sm">
      <h3 className="text-sm font-bold uppercase tracking-wider text-slate-600">{title}</h3>
      <div className="mt-3">{children}</div>
    </div>
  );
}

export function DailyClosePage() {
  const { t } = useI18n();
  const money = useMoney();
  const { selectedStoreId, availableStores, setSelectedStoreById } = useStoreSelectionStore();

  // Để trống là CÓ Ý: BE sẽ lấy hôm qua theo múi giờ của cửa hàng. Client không
  // biết múi giờ đó nên tự điền "hôm qua theo giờ máy" là đoán sai.
  const [date, setDate] = useState('');

  const { data: report, isLoading, error } = useDailyClose({
    store_id: selectedStoreId ?? undefined,
    date: date || undefined,
  });

  const isTimezoneGuessed = report?.timezone?.source === 'fallback';
  const revenueRows = report?.revenue ?? [];
  const vatRows = report?.vat_by_rate ?? [];
  const statusEntries = Object.entries(report?.orders?.by_status ?? {});

  return (
    <PageContainer>
      <PageHeader title={t('dailyCloseTitle')} subtitle={t('dailyCloseSubtitle')} />

      {/* Bộ lọc: cửa hàng + ngày */}
      <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
        <div className="flex w-full items-center gap-2 sm:w-auto">
          <span className="text-xs font-medium text-[#434655] sm:text-sm">
            {t('dailyCloseStore')}:
          </span>
          <select
            value={selectedStoreId ?? ''}
            onChange={(e) => setSelectedStoreById(e.target.value || null)}
            className="h-9 flex-1 rounded-lg border border-[#E2E8F0] bg-white px-3 pr-8 text-xs text-[#191B23] outline-none focus:ring-2 focus:ring-[#004AC6]/20 sm:flex-none sm:text-sm"
          >
            {/* Không có lựa chọn "tất cả": báo cáo này chỉ tồn tại theo từng cửa hàng. */}
            <option value="">{t('dailyCloseSelectStore')}</option>
            {availableStores.map((store) => (
              <option key={store.id} value={store.id}>
                {store.name}
              </option>
            ))}
          </select>
        </div>

        <div className="flex w-full items-center gap-2 sm:w-auto">
          <span className="text-xs font-medium text-[#434655] sm:text-sm">
            {t('dailyCloseDate')}:
          </span>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="h-9 flex-1 rounded-lg border border-[#E2E8F0] bg-white px-3 text-xs text-[#191B23] outline-none focus:ring-2 focus:ring-[#004AC6]/20 sm:flex-none sm:text-sm"
          />
          <span className="hidden text-xs text-slate-400 lg:inline">{t('dailyCloseDateHint')}</span>
        </div>
      </div>

      {!selectedStoreId ? (
        <div className="mt-6 rounded-xl border border-dashed border-slate-200 bg-slate-50/50 py-12 text-center text-sm text-slate-400">
          {t('dailyClosePickStorePrompt')}
        </div>
      ) : error ? (
        <div className="mt-6 rounded-lg bg-red-50 p-4 text-sm text-red-700">
          <p className="font-medium">{t('dailyCloseLoadFailed')}</p>
          <p className="mt-1 text-xs text-red-600">{error instanceof Error ? error.message : ''}</p>
        </div>
      ) : isLoading ? (
        <div className="flex items-center justify-center py-16">
          <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary-600" />
        </div>
      ) : !report ? null : (
        <>
          {/* Múi giờ ĐOÁN: con số vẫn ra bình thường nhưng ranh giới ngày có thể
              sai, nên không được im lặng ở đây. */}
          {isTimezoneGuessed && (
            <div className="mt-5 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
              <WarningIcon className="mt-0.5 shrink-0 text-amber-700" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-amber-900">
                  {t('dailyCloseTzFallbackTitle')}
                </p>
                <p className="mt-0.5 text-xs text-amber-800">
                  {t('dailyCloseTzFallbackBody', { timezone: report.timezone.name })}
                </p>
              </div>
            </div>
          )}

          {/* Bối cảnh của con số: ngày nào, múi giờ nào, cửa sổ UTC thật là gì. */}
          <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="rounded-xl border border-slate-100 bg-white p-4 shadow-sm">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                {t('dailyCloseDate')}
              </p>
              <p className="mt-1 text-lg font-bold text-slate-900">{report.date}</p>
              <p className="mt-1 text-xs text-slate-400">
                {report.store?.name ?? report.store?.code ?? ''}
              </p>
            </div>
            <div className="rounded-xl border border-slate-100 bg-white p-4 shadow-sm">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                {t('dailyCloseTimezone')}
              </p>
              <p className="mt-1 text-lg font-bold text-slate-900">{report.timezone.name}</p>
              <p className={`mt-1 text-xs ${isTimezoneGuessed ? 'text-amber-700' : 'text-slate-400'}`}>
                {isTimezoneGuessed ? t('dailyCloseTzSourceFallback') : t('dailyCloseTzSourceStore')}
              </p>
            </div>
            <div className="rounded-xl border border-slate-100 bg-white p-4 shadow-sm">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                {t('dailyCloseWindowUtc')}
              </p>
              <p className="mt-1 break-all text-xs font-medium text-slate-700">
                {report.window_utc.start}
              </p>
              <p className="break-all text-xs font-medium text-slate-700">{report.window_utc.end}</p>
            </div>
          </div>

          <p className="mt-3 text-xs text-slate-400">{t('dailyCloseDiffersNote')}</p>

          {/* Đơn hàng */}
          <div className="mt-6">
            <SectionCard title={t('dailyCloseOrders')}>
              <div className="flex flex-wrap items-center gap-6">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    {t('dailyCloseTotalOrders')}
                  </p>
                  <p className="mt-1 text-2xl font-bold text-slate-900">
                    {(report.orders?.total ?? 0).toLocaleString()}
                  </p>
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    {t('dailyCloseByStatus')}
                  </p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {statusEntries.length === 0 ? (
                      <span className="text-sm text-slate-400">{t('dailyCloseNoOrders')}</span>
                    ) : (
                      statusEntries.map(([status, count]) => (
                        <span
                          key={status}
                          className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700"
                        >
                          {status}
                          <span className="font-bold text-slate-900">{count.toLocaleString()}</span>
                        </span>
                      ))
                    )}
                  </div>
                </div>
              </div>
            </SectionCard>
          </div>

          {/* Doanh thu theo tiền tệ — mỗi dòng một loại tiền, KHÔNG cộng gộp.
              Vì thế format theo `row.currency` chứ không theo scope của trang. */}
          <div className="mt-6">
            <SectionCard title={t('dailyCloseRevenue')}>
              {revenueRows.length === 0 ? (
                <p className="py-6 text-center text-sm text-slate-400">{t('dailyCloseNoRevenue')}</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-slate-200 text-sm">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-slate-600">
                          {t('dailyCloseCurrency')}
                        </th>
                        <th className="px-4 py-2.5 text-right text-xs font-semibold uppercase tracking-wider text-slate-600">
                          {t('dailyCloseOrderCount')}
                        </th>
                        <th className="px-4 py-2.5 text-right text-xs font-semibold uppercase tracking-wider text-slate-600">
                          {t('dailyCloseGross')}
                        </th>
                        <th className="px-4 py-2.5 text-right text-xs font-semibold uppercase tracking-wider text-slate-600">
                          {t('dailyCloseTax')}
                        </th>
                        <th className="px-4 py-2.5 text-right text-xs font-semibold uppercase tracking-wider text-slate-600">
                          {t('dailyCloseNet')}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {revenueRows.map((row) => (
                        <tr key={row.currency} className="transition-colors hover:bg-slate-50">
                          <td className="px-4 py-3 font-medium text-slate-900">{row.currency}</td>
                          <td className="px-4 py-3 text-right text-slate-600">
                            {row.order_count.toLocaleString()}
                          </td>
                          <td className="px-4 py-3 text-right text-slate-900">
                            {money.format(toNumber(row.gross), { currency: row.currency })}
                          </td>
                          <td className="px-4 py-3 text-right text-slate-600">
                            {money.format(toNumber(row.tax), { currency: row.currency })}
                          </td>
                          <td className="px-4 py-3 text-right font-semibold text-slate-900">
                            {money.format(toNumber(row.net), { currency: row.currency })}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </SectionCard>
          </div>

          {/* VAT theo thuế suất.
              BE không gắn currency vào từng dòng VAT — khi trong ngày có nhiều
              loại tiền thì con số ở đây là gộp, nên hiển thị TRUNG TÍNH (không
              gắn ký hiệu tiền tệ) để không khẳng định sai một loại tiền nào. */}
          <div className="mt-6">
            <SectionCard title={t('dailyCloseVat')}>
              {vatRows.length === 0 ? (
                <p className="py-6 text-center text-sm text-slate-400">{t('dailyCloseNoVat')}</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-slate-200 text-sm">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-slate-600">
                          {t('dailyCloseVatRate')}
                        </th>
                        <th className="px-4 py-2.5 text-right text-xs font-semibold uppercase tracking-wider text-slate-600">
                          {t('dailyCloseGross')}
                        </th>
                        <th className="px-4 py-2.5 text-right text-xs font-semibold uppercase tracking-wider text-slate-600">
                          {t('dailyCloseTax')}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {vatRows.map((row) => (
                        <tr key={String(row.tax_rate)} className="transition-colors hover:bg-slate-50">
                          <td className="px-4 py-3 font-medium text-slate-900">
                            {toNumber(row.tax_rate)}%
                          </td>
                          <td className="px-4 py-3 text-right text-slate-900">
                            {toNumber(row.gross).toLocaleString()}
                          </td>
                          <td className="px-4 py-3 text-right text-slate-600">
                            {toNumber(row.tax).toLocaleString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </SectionCard>
          </div>

          {/* Thanh toán theo tiền tệ (mục #229). Optional trên type — trước khi
              BE lên QA field này vắng mặt, ẩn hẳn block thay vì hiện bảng rỗng
              để không ai tưởng nhầm "ngày này không có giao dịch nào". */}
          {report.payments && (
            <div className="mt-6">
              <SectionCard title={t('dailyClosePayments')}>
                {report.payments.by_currency.length === 0 ? (
                  <p className="py-6 text-center text-sm text-slate-400">
                    {t('dailyCloseNoPayments')}
                  </p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-slate-200 text-sm">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-slate-600">
                            {t('dailyCloseCurrency')}
                          </th>
                          <th className="px-4 py-2.5 text-right text-xs font-semibold uppercase tracking-wider text-slate-600">
                            {t('dailyCloseTransactionCount')}
                          </th>
                          <th className="px-4 py-2.5 text-right text-xs font-semibold uppercase tracking-wider text-slate-600">
                            {t('dailyClosePaidAmount')}
                          </th>
                          <th className="px-4 py-2.5 text-right text-xs font-semibold uppercase tracking-wider text-slate-600">
                            {t('dailyCloseRefundedAmount')}
                          </th>
                          <th className="px-4 py-2.5 text-right text-xs font-semibold uppercase tracking-wider text-slate-600">
                            {t('dailyCloseNetCollected')}
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {report.payments.by_currency.map((row) => (
                          <tr key={row.currency} className="transition-colors hover:bg-slate-50">
                            <td className="px-4 py-3 font-medium text-slate-900">{row.currency}</td>
                            <td className="px-4 py-3 text-right text-slate-600">
                              {row.transaction_count.toLocaleString()}
                            </td>
                            <td className="px-4 py-3 text-right text-slate-900">
                              {money.format(toNumber(row.paid_amount), { currency: row.currency })}
                            </td>
                            <td className="px-4 py-3 text-right text-slate-600">
                              {money.format(toNumber(row.refunded_amount), { currency: row.currency })}
                            </td>
                            <td className="px-4 py-3 text-right font-semibold text-slate-900">
                              {money.format(toNumber(row.net_collected), { currency: row.currency })}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </SectionCard>
            </div>
          )}

          {/* Lệch tồn trong ngày (mục #229). `items` tối đa 20 dòng, đã sắp
              theo độ lệch giảm dần — không tự sắp lại ở đây. */}
          {report.inventory_drift && (
            <div className="mt-6">
              <SectionCard title={t('dailyCloseInventoryDrift')}>
                <p className="text-xs text-slate-400">
                  {t('dailyCloseInventoryDriftSummary', {
                    checked: report.inventory_drift.checked_pairs,
                    drift: report.inventory_drift.drift_count,
                  })}
                </p>
                {report.inventory_drift.items.length === 0 ? (
                  <p className="py-6 text-center text-sm text-slate-400">
                    {t('dailyCloseNoDrift')}
                  </p>
                ) : (
                  <div className="mt-3 overflow-x-auto">
                    <table className="min-w-full divide-y divide-slate-200 text-sm">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-slate-600">
                            {t('dailyCloseDriftSku')}
                          </th>
                          <th className="px-4 py-2.5 text-right text-xs font-semibold uppercase tracking-wider text-slate-600">
                            {t('dailyCloseDriftExpected')}
                          </th>
                          <th className="px-4 py-2.5 text-right text-xs font-semibold uppercase tracking-wider text-slate-600">
                            {t('dailyCloseDriftActual')}
                          </th>
                          <th className="px-4 py-2.5 text-right text-xs font-semibold uppercase tracking-wider text-slate-600">
                            {t('dailyCloseDriftDifference')}
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {report.inventory_drift.items.map((item) => (
                          <tr key={item.product_id} className="transition-colors hover:bg-slate-50">
                            <td className="px-4 py-3 font-medium text-slate-900">{item.product_sku}</td>
                            <td className="px-4 py-3 text-right text-slate-600">
                              {toNumber(item.store_expected).toLocaleString()}
                            </td>
                            <td className="px-4 py-3 text-right text-slate-600">
                              {toNumber(item.store_actual).toLocaleString()}
                            </td>
                            <td className="px-4 py-3 text-right font-semibold text-amber-700">
                              {toNumber(item.difference).toLocaleString()}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </SectionCard>
            </div>
          )}

          {/* Đối soát doanh thu ↔ thanh toán (mục #229). BE tính sẵn — không tự
              trừ lại ở FE, chỉ hiển thị đúng con số + status BE trả. */}
          {report.reconciliation && (
            <div className="mt-6">
              <SectionCard title={t('dailyCloseReconciliation')}>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-slate-200 text-sm">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-slate-600">
                          {t('dailyCloseCurrency')}
                        </th>
                        <th className="px-4 py-2.5 text-right text-xs font-semibold uppercase tracking-wider text-slate-600">
                          {t('dailyCloseReconRevenue')}
                        </th>
                        <th className="px-4 py-2.5 text-right text-xs font-semibold uppercase tracking-wider text-slate-600">
                          {t('dailyCloseReconPayments')}
                        </th>
                        <th className="px-4 py-2.5 text-right text-xs font-semibold uppercase tracking-wider text-slate-600">
                          {t('dailyCloseReconDifference')}
                        </th>
                        <th className="px-4 py-2.5 text-right text-xs font-semibold uppercase tracking-wider text-slate-600">
                          {t('dailyCloseReconStatus')}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {report.reconciliation.by_currency.map((row) => (
                        <tr key={row.currency} className="transition-colors hover:bg-slate-50">
                          <td className="px-4 py-3 font-medium text-slate-900">{row.currency}</td>
                          <td className="px-4 py-3 text-right text-slate-900">
                            {money.format(toNumber(row.revenue_gross), { currency: row.currency })}
                          </td>
                          <td className="px-4 py-3 text-right text-slate-900">
                            {money.format(toNumber(row.payments_net), { currency: row.currency })}
                          </td>
                          <td className="px-4 py-3 text-right font-semibold text-slate-900">
                            {money.format(toNumber(row.difference), { currency: row.currency })}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <span
                              className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${
                                row.status === 'MATCHED'
                                  ? 'bg-green-100 text-green-700'
                                  : 'bg-red-100 text-red-700'
                              }`}
                            >
                              {row.status === 'MATCHED'
                                ? t('dailyCloseReconMatched')
                                : t('dailyCloseReconMismatch')}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="mt-4 flex items-center gap-2 border-t border-slate-100 pt-4">
                  <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    {t('dailyCloseReconInventoryStatus')}
                  </span>
                  <span
                    className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${
                      report.reconciliation.inventory.status === 'OK'
                        ? 'bg-green-100 text-green-700'
                        : 'bg-red-100 text-red-700'
                    }`}
                  >
                    {report.reconciliation.inventory.status === 'OK'
                      ? t('dailyCloseReconInventoryOk')
                      : t('dailyCloseReconInventoryDrift', {
                          count: report.reconciliation.inventory.drift_count,
                        })}
                  </span>
                </div>
              </SectionCard>
            </div>
          )}
        </>
      )}
    </PageContainer>
  );
}
